import { ref, shallowRef, watch } from 'vue'
import type { PagePresence } from '~/pageTools'

/** The longest an action waits for the cursor: a decoration never holds the agent up for long. */
const ARRIVAL_MAX_MS = 800

/**
 * The agent's presence on the page while it acts (spec
 * `page-tools-improvements.md`, M12): the element the page tools point at
 * before acting on it, for `PkAgentPresence`. `point` resolves once the
 * cursor clicked there, or after a short while at most; the last element
 * stays the target between two actions, until control of the page ends.
 * `toward` makes the next element the target ahead of its action, `heading`
 * there with no click. `clicks` counts the cursor's clicks, for the aurora's
 * glints.
 */
export function usePagePresence(active: () => boolean) {
    const target = shallowRef<{
        element: Element
        name: string
        heading?: boolean
    }>()
    const clicks = ref(0)
    let reach: (() => void) | undefined

    const point: PagePresence['point'] = (element, name) =>
        new Promise<void>((resolve) => {
            reach?.()
            const timer = setTimeout(() => done(), ARRIVAL_MAX_MS)
            const done = () => {
                clearTimeout(timer)
                if (reach === done) {
                    reach = undefined
                }
                resolve()
            }
            reach = done
            target.value = { element, name }
        })

    const toward: NonNullable<PagePresence['toward']> = (element, name) => {
        // An action waiting for the cursor keeps it
        if (!reach) {
            target.value = { element, name, heading: true }
        }
    }

    /** The action can run: the cursor clicked on the target, or has nothing to show. */
    const arrived = (clicked: boolean) => {
        if (reach && clicked) {
            clicks.value++
        }
        reach?.()
    }

    watch(active, (on) => {
        if (!on) {
            reach?.()
            target.value = undefined
        }
    })

    return { target, clicks, point, toward, arrived }
}
